import React, { useState, useEffect } from 'react';

const ExistingDynamicPricingRules = ({ dynamicPricingList, handleUpdatePricing, handleDeletePricing }) => {
    const [randomDiscountStates, setRandomDiscountStates] = useState({});
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [feedbackMessage, setFeedbackMessage] = useState('');

    // Initialize state for each product
    useEffect(() => {
        const initialState = dynamicPricingList.reduce((acc, pricing) => {
            acc[pricing._id] = {
                enabled: pricing.isRandomDiscountEnabled || false,
                percentage: pricing.discountPercentage || '',
                minPrice: pricing.minPrice || '',
                frequency: pricing.discountFrequency || '',
            };
            return acc;
        }, {});
        setRandomDiscountStates(initialState);
    }, [dynamicPricingList]);

    const handleToggleRandomDiscount = (productId) => {
        setRandomDiscountStates((prevStates) => {
            const updatedState = { ...prevStates };
            updatedState[productId].enabled = !updatedState[productId].enabled;
            return updatedState;
        });
        setSelectedProductId(productId);  // Ensure productId is used here
    };

    const handleFormFieldChange = (productId, field, value) => {
        setRandomDiscountStates((prevStates) => ({
            ...prevStates,
            [productId]: {
                ...prevStates[productId],
                [field]: value,
            },
        }));
    };

    const handleSaveDiscount = () => {
        if (selectedProductId) {
            const { percentage, minPrice, frequency, enabled } = randomDiscountStates[selectedProductId];
            if (!percentage || !minPrice || !frequency) {
                setFeedbackMessage('Please fill out all fields before saving.');
                return;
            }

            const payload = { percentage, minPrice, frequency, isRandomDiscountEnabled: enabled };
            fetch(`/api/dynamic-pricing/products/${selectedProductId}/discount`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })
                .then((response) => response.json())
                .then((data) => {
                    setFeedbackMessage('Discount applied successfully!');
                    console.log(data);
                })
                .catch((error) => {
                    setFeedbackMessage('Error applying discount.');
                    console.error('Error:', error);
                });
        }
    };

    return (
        <div className="mt-8 p-6 rounded-lg shadow-lg bg-white">
            <h3 className="text-2xl font-semibold text-gray-700 mb-6">Existing Dynamic Pricing Rules</h3>

            {/* Display feedback message */}
            {feedbackMessage && (
                <div className="mb-4 text-sm text-green-600">{feedbackMessage}</div>
            )}

            {/* Form Fields and Save Button Outside the Table */}
            {selectedProductId && (
                <div className="mt-4 space-y-3">
                    <h4 className="text-lg font-semibold text-gray-700">Edit Discount for Product</h4>
                    <div className="grid grid-cols-3 gap-6">
                        {/* Discount Percentage Field */}
                        <div>
                            <label className="text-sm font-medium text-gray-700" htmlFor="discountPercentage">Discount Percentage (%)</label>
                            <input
                                id="discountPercentage"
                                type="number"
                                value={randomDiscountStates[selectedProductId]?.percentage || ''}
                                onChange={(e) => handleFormFieldChange(selectedProductId, 'percentage', e.target.value)}
                                className="block w-full px-6 py-4 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter Discount Percentage"
                            />
                        </div>

                        {/* Min Price Field */}
                        <div>
                            <label className="text-sm font-medium text-gray-700" htmlFor="minPrice">Min Price ($)</label>
                            <input
                                id="minPrice"
                                type="number"
                                value={randomDiscountStates[selectedProductId]?.minPrice || ''}
                                onChange={(e) => handleFormFieldChange(selectedProductId, 'minPrice', e.target.value)}
                                className="block w-full px-6 py-4 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter Min Price"
                            />
                        </div>

                        {/* Discount Frequency Dropdown */}
                        <div>
                            <label className="text-sm font-medium text-gray-700" htmlFor="frequency">Discount Frequency</label>
                            <select
                                id="frequency"
                                value={randomDiscountStates[selectedProductId]?.frequency || ''}
                                onChange={(e) => handleFormFieldChange(selectedProductId, 'frequency', e.target.value)}
                                className="block w-full px-6 py-4 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Frequency</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>

                        {/* Enable/Disable Random Discount Dropdown */}
                        <div>
                            <label className="text-sm font-medium text-gray-700" htmlFor="enableRandomDiscount">Enable Random Discount</label>
                            <select
                                id="enableRandomDiscount"
                                value={randomDiscountStates[selectedProductId]?.enabled ? 'enabled' : 'disabled'}
                                onChange={(e) => {
                                    handleFormFieldChange(selectedProductId, 'enabled', e.target.value === 'enabled');
                                }}
                                className="block w-full px-6 py-4 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="enabled">Enabled</option>
                                <option value="disabled">Disabled</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={handleSaveDiscount}
                            className="bg-blue-500 text-white py-3 px-6 rounded-md transition hover:bg-blue-600"
                        >
                            Save Discount
                        </button>
                    </div>
                </div>
            )}

            <div className="overflow-x-auto bg-white mt-6">
                <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Product</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Base Price</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Sale Price</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Condition</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Start Date</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">End Date</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Min Price</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Discount Percentage</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Random Discount Enabled</th> {/* New Column */}
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">FreeMonths</th> {/* New Column */}
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Discount Fields</th>
                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-gray-700">
                        {dynamicPricingList.map((pricing) => (
                            <tr key={pricing._id} className="hover:bg-gray-50">
                                <td className="py-4 px-6">{pricing.productName}</td>
                                <td className="py-4 px-6">${pricing.basePrice}</td>
                                <td className="py-4 px-6">${pricing.salePrice}</td>
                                <td className="py-4 px-6">{pricing.condition}</td>
                                <td className="py-4 px-6">{pricing.startDate}</td>
                                <td className="py-4 px-6">{pricing.endDate}</td>
                                <td className="py-4 px-6">${pricing.minPrice}</td>
                                <td className="py-4 px-6">{pricing.randomDiscountPercent || 'N/A'}</td>
                                <td className="py-4 px-6">{randomDiscountStates[pricing._id]?.enabled ? 'Yes' : 'No'}</td> {/* Display Random Discount Status */}
                                <td className="py-4 px-6">{pricing.freeMonths || 'N/A'}</td>

                                <td className="py-4 px-6">
                                    <div className="flex items-center">
                                        <label className="mr-2 text-gray-600 text-sm">Use Random Discount</label>
                                        <input
                                            type="checkbox"
                                            checked={randomDiscountStates[pricing._id]?.enabled || false}
                                            onChange={() => {
                                                handleToggleRandomDiscount(pricing._id);
                                            }}
                                            className="toggle-checkbox appearance-none h-8 w-14 bg-gray-300 rounded-full cursor-pointer relative"
                                        />
                                    </div>
                                </td>

                                <td className="py-4 px-6 flex space-x-2">
                                    <button
                                        onClick={() => handleUpdatePricing(pricing._id)}
                                        className="bg-yellow-500 text-white py-3 px-6 rounded-md transition hover:bg-yellow-600"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeletePricing(pricing._id)}
                                        className="bg-red-500 text-white py-3 px-6 rounded-md transition hover:bg-red-600"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ExistingDynamicPricingRules;
